import type { TFunction } from "i18next";
import type { SettlementFlowKind, SettlementLineRow } from "@/services/adminSettlementService";
import { needsSupplierInvoice } from "./settlementUi";

export type SettlementWorkflowStepId = "summary" | "collect" | "supplier" | "invoice" | "pay";

export interface SettlementWorkflowStep {
  id: SettlementWorkflowStepId;
  index: number;
  completed: boolean;
  available: boolean;
}

export function workflowStepsForFlow(flow: SettlementFlowKind): SettlementWorkflowStepId[] {
  switch (flow) {
    case "RECEIPT_BASED":
      return ["summary", "collect", "invoice"];
    case "SUPPLIER_PAYOUT":
      return ["summary", "supplier", "pay"];
    case "UPFRONT":
      return ["invoice", "pay"];
  }
}

function isSummaryComplete(lines: SettlementLineRow[]): boolean {
  const relevant = lines.filter((line) => line.invoice_status !== "INVOICED");
  if (relevant.length === 0) return true;
  return relevant.every((line) => line.summary_email_count > 0);
}

function isCollectionComplete(lines: SettlementLineRow[]): boolean {
  return !lines.some(
    (line) => line.collection_status === "UNCOLLECTED" && line.invoiceable_amount_cents > 0,
  );
}

function isSupplierInvoiceComplete(lines: SettlementLineRow[]): boolean {
  return !lines.some(needsSupplierInvoice);
}

function isCommissionInvoiceComplete(lines: SettlementLineRow[]): boolean {
  const relevant = lines.filter(
    (line) =>
      (line.flow_kind === "UPFRONT" || line.flow_kind === "RECEIPT_BASED") &&
      line.invoiceable_amount_cents > 0,
  );
  if (relevant.length === 0) return true;
  return relevant.every((line) => line.invoice_status === "INVOICED");
}

function isPaymentComplete(lines: SettlementLineRow[]): boolean {
  const relevant = lines.filter(
    (line) => line.payment_status !== "NOT_APPLICABLE" && line.payable_amount_cents > 0,
  );
  if (relevant.length === 0) return true;
  return relevant.every((line) => line.payment_status === "PAID");
}

function isStepCompleted(id: SettlementWorkflowStepId, lines: SettlementLineRow[]): boolean {
  switch (id) {
    case "summary":
      return isSummaryComplete(lines);
    case "collect":
      return isCollectionComplete(lines);
    case "supplier":
      return isSupplierInvoiceComplete(lines);
    case "invoice":
      return isCommissionInvoiceComplete(lines);
    case "pay":
      return isPaymentComplete(lines);
  }
}

function isStepAvailable(
  id: SettlementWorkflowStepId,
  flow: SettlementFlowKind,
  lines: SettlementLineRow[],
): boolean {
  switch (id) {
    case "summary":
      return flow === "SUPPLIER_PAYOUT" || flow === "RECEIPT_BASED";
    case "collect":
      if (flow === "RECEIPT_BASED") {
        return isSummaryComplete(lines);
      }
      return true;
    case "supplier":
      return isSummaryComplete(lines);
    case "invoice":
      if (flow === "RECEIPT_BASED") {
        return isCollectionComplete(lines);
      }
      return true;
    case "pay":
      if (flow === "SUPPLIER_PAYOUT") {
        return isSupplierInvoiceComplete(lines);
      }
      if (lines.some((line) => line.invoiceable_amount_cents > 0)) {
        return isCommissionInvoiceComplete(lines);
      }
      return true;
  }
}

export function buildSettlementWorkflowSteps(
  flow: SettlementFlowKind,
  lines: SettlementLineRow[],
): SettlementWorkflowStep[] {
  const ids = workflowStepsForFlow(flow);
  return ids.map((id, idx) => ({
    id,
    index: idx + 1,
    completed: isStepCompleted(id, lines),
    available: isStepAvailable(id, flow, lines),
  }));
}

export function defaultActiveWorkflowStep(steps: SettlementWorkflowStep[]): SettlementWorkflowStepId {
  const firstIncomplete = steps.find((step) => !step.completed);
  if (firstIncomplete) return firstIncomplete.id;
  return steps[steps.length - 1]?.id ?? steps[0]?.id ?? "pay";
}

export function workflowStepUnavailableMessage(
  id: SettlementWorkflowStepId,
  t: TFunction<"payments">,
): string {
  switch (id) {
    case "summary":
      return t("settlements.detail.steps.unavailableSummary");
    case "collect":
      return t("settlements.detail.steps.unavailableCollect");
    case "supplier":
      return t("settlements.detail.steps.unavailableSupplier");
    case "invoice":
      return t("settlements.detail.steps.unavailableInvoice");
    case "pay":
      return t("settlements.detail.steps.unavailablePay");
  }
}
